import { assets } from './assets';
import type { Map } from './backend';
import { enumIs } from './enums';
import type { MapBrush, MapHouse } from './game';
import { unreachable } from './unreachable';

export const TILE_SIZE = 20;

function sampleArray<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function renderMap(map: Map): {
  ground: HTMLCanvasElement;
  brushes: MapBrush[];
  houses: MapHouse[];
} {
  const canvas = document.createElement('canvas');
  canvas.width = Number(map.width) * TILE_SIZE;
  canvas.height = Number(map.height) * TILE_SIZE;

  const context = canvas.getContext('2d');
  context.clearRect(0, 0, canvas.width, canvas.height);

  const brushIdMap = new Map<
    number,
    {
      xMin: number;
      yMin: number;
      xMax: number;
      yMax: number;
    }
  >();

  const houseIdMap = new Map<
    number,
    {
      xMin: number;
      yMin: number;
      xMax: number;
      yMax: number;
    }
  >();

  for (let x = 0; x < map.height; x++) {
    for (let y = 0; y < map.width; y++) {
      context.fillStyle = 'black';

      const tile = map.tiles[y][x];

      if (enumIs(tile, 'Empty')) {
        context.drawImage(
          sampleArray(assets.images.grass),
          x * TILE_SIZE,
          y * TILE_SIZE,
          TILE_SIZE,
          TILE_SIZE
        );
      } else if (enumIs(tile, 'Brush')) {
        const brushId = tile.Brush.id;
        const brush = brushIdMap.get(brushId) ?? {
          xMin: x,
          yMin: y,
          xMax: x,
          yMax: y,
        };

        brushIdMap.set(brushId, {
          xMin: Math.min(brush.xMin, x),
          yMin: Math.min(brush.yMin, y),
          xMax: Math.max(brush.xMax, x),
          yMax: Math.max(brush.yMax, y),
        });
        context.drawImage(
          sampleArray(assets.images.grass),
          x * TILE_SIZE,
          y * TILE_SIZE,
          TILE_SIZE,
          TILE_SIZE
        );
      } else if (enumIs(tile, 'Impassable')) {
        context.drawImage(
          sampleArray(assets.images.rock),
          x * TILE_SIZE,
          y * TILE_SIZE,
          TILE_SIZE,
          TILE_SIZE
        );
      } else if (enumIs(tile, 'Wall')) {
        context.fillStyle = '#ffffff';
        context.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
      } else if (enumIs(tile, 'HouseDoor')) {
        context.fillStyle = '#e0e0e0';
        context.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
      } else if (enumIs(tile, 'Indoors')) {
        const houseId = tile.Indoors.id;
        context.fillStyle = '#e0e0e0';
        context.fillRect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);

        const brush = houseIdMap.get(houseId) ?? {
          xMin: x,
          yMin: y,
          xMax: x,
          yMax: y,
        };

        houseIdMap.set(houseId, {
          xMin: Math.min(brush.xMin, x),
          yMin: Math.min(brush.yMin, y),
          xMax: Math.max(brush.xMax, x),
          yMax: Math.max(brush.yMax, y),
        });
      } else {
        unreachable(tile);
      }
    }
  }

  const brushes: MapBrush[] = [];

  for (const brushId of brushIdMap.keys()) {
    const brush = brushIdMap.get(brushId)!;
    const image = sampleArray(assets.images.brush);
    const brushCanvas = document.createElement('canvas');
    brushCanvas.width = (brush.xMax - brush.xMin + 1) * TILE_SIZE;
    brushCanvas.height = (brush.yMax - brush.yMin + 1) * TILE_SIZE;
    const brushContext = brushCanvas.getContext('2d')!;
    for (let x = brush.xMin; x <= brush.xMax; x++) {
      for (let y = brush.yMin; y <= brush.yMax; y++) {
        const tile = map.tiles[y][x];
        if (enumIs(tile, 'Brush') && tile.Brush.id == brushId) {
          brushContext.drawImage(
            image,
            (x - brush.xMin) * TILE_SIZE,
            (y - brush.yMin) * TILE_SIZE,
            TILE_SIZE,
            TILE_SIZE
          );
        }
      }
    }

    brushes.push({
      x: brush.xMin,
      y: brush.yMin,
      w: brush.xMax - brush.xMin + 1,
      h: brush.yMax - brush.yMin + 1,
      image: brushCanvas,
      id: brushId,
    });
  }

  const houses: MapHouse[] = [];

  for (const houseId of houseIdMap.keys()) {
    const house = houseIdMap.get(houseId)!;
    const houseCanvas = document.createElement('canvas');
    houseCanvas.width = (house.xMax - house.xMin + 1) * TILE_SIZE;
    houseCanvas.height = (house.yMax - house.yMin + 1) * TILE_SIZE;
    const houseContext = houseCanvas.getContext('2d')!;

    houseContext.fillStyle = 'black';
    for (let x = house.xMin; x <= house.xMax; x++) {
      for (let y = house.yMin; y <= house.yMax; y++) {
        const tile = map.tiles[y][x];
        if (enumIs(tile, 'Indoors') && tile.Indoors.id == houseId) {
          houseContext.fillRect(
            (x - house.xMin) * TILE_SIZE,
            (y - house.yMin) * TILE_SIZE,
            TILE_SIZE,
            TILE_SIZE
          );
        }
      }
    }

    houses.push({
      x: house.xMin,
      y: house.yMin,
      w: house.xMax - house.xMin + 1,
      h: house.yMax - house.yMin + 1,
      image: houseCanvas,
      id: houseId,
    });
  }

  return {
    ground: canvas,
    brushes,
    houses,
  };
}
